/**
 * ConnectedSourcesPill — compact provider-status indicator for the TopBar /
 * dashboard / module headers.
 *
 * Renders a row of tiny dot+label chips, one per integration provider
 * the customer has connected (Tally / Zoho / GST / Odoo / Razorpay / Excel).
 *
 * Data source: `/api/customer/dashboard-data` → `params.integrations_connected`
 * + `data_source` (the provider that fed the active financial snapshot).
 * The provider that fed the *latest* snapshot is highlighted in emerald.
 */
import React from "react";
import { CheckCircle2, AlertCircle } from "lucide-react";

const PROVIDERS = [
  { id: "tally",    label: "Tally" },
  { id: "zoho",     label: "Zoho" },
  { id: "gst",      label: "GST" },
  { id: "odoo",     label: "Odoo" },
  { id: "razorpay", label: "Razorpay" },
  { id: "excel",    label: "Excel" },
];

export default function ConnectedSourcesPill({
  integrationsConnected = {},
  activeSource = null,        // string — e.g. "zoho" — provider feeding current snapshot
  testid = "connected-sources-pill",
  compact = false,            // only show connected; otherwise show all
}) {
  const ic = integrationsConnected || {};
  const visible = compact ? PROVIDERS.filter((p) => ic[p.id]) : PROVIDERS;
  if (visible.length === 0) {
    return (
      <div
        data-testid={`${testid}-empty`}
        className="inline-flex items-center gap-1.5 rounded-full bg-amber-50 px-2.5 py-1 text-[10.5px] font-semibold text-amber-800 ring-1 ring-amber-200"
      >
        <AlertCircle size={11} /> No data sources connected
      </div>
    );
  }

  return (
    <div data-testid={testid} className="inline-flex flex-wrap items-center gap-1.5">
      {visible.map((p) => {
        const isConnected = !!ic[p.id];
        const isActive = activeSource && activeSource.toLowerCase() === p.id;
        const cls = isActive
          ? "bg-emerald-50 text-emerald-800 ring-emerald-300"
          : isConnected
          ? "bg-slate-50 text-slate-700 ring-slate-200"
          : "bg-slate-100/60 text-slate-400 ring-slate-200";
        return (
          <span
            key={p.id}
            data-testid={`${testid}-${p.id}`}
            title={
              isActive
                ? `${p.label} · driving the dashboard right now`
                : isConnected
                ? `${p.label} · connected`
                : `${p.label} · not connected`
            }
            className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-semibold ring-1 ${cls}`}
          >
            <span
              className={`h-1.5 w-1.5 rounded-full ${
                isActive ? "bg-emerald-500 animate-pulse" : isConnected ? "bg-slate-500" : "bg-slate-300"
              }`}
            />
            {p.label}
            {isActive && <CheckCircle2 size={9} className="text-emerald-600" />}
          </span>
        );
      })}
    </div>
  );
}
